import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { nextPlan, tokenBalance, usePlansQuery, useQuotaQuery } from '@entities/Plan';
// Не из индексов фичи: они тянут экран игры. Здесь нужны только данные из кэша.
import type { LondonState } from '@features/London/api/londonApi';
import { londonKeys } from '@features/London/model/queries';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { Button, TokenIcon } from '@shared/ui';

/**
 * Тихая строка про тариф в «На сегодня» (владелец 2026-11-04): только в день, когда жетоны или силы в игре кончились.
 * Силы читаем из кэша, без запроса: `/london/state` заводит сохранение тем, кто ещё не играл.
 */
export function PlanHintRow() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const quota = useQuotaQuery().data;
  const plans = usePlansQuery().data;
  const game = qc.getQueryData<LondonState>(londonKeys.state);
  const next = quota ? nextPlan(plans, quota.plan) : null;
  const balance = quota ? tokenBalance(quota) : null;
  const tokensOut = Boolean(balance?.out && balance.bonus === 0);
  const energyOut = Boolean(game && game.energy.left <= 0);
  const reason = !next ? null : tokensOut ? 'tokens' : energyOut ? 'energy' : null;

  useEffect(() => {
    if (reason) trackEvent('tariff_hint_shown', { place: 'home', reason });
  }, [reason]);

  if (!next || !reason) return null;
  const limit = reason === 'tokens' ? next.dailyGenerationLimit : game?.energy.next_plan?.max;

  return (
    <div className="dl-row flex items-center gap-3">
      <span aria-hidden className="dl-art lx-px-sm grid h-12 w-12 flex-none place-items-center bg-[#2a2116]">
        <TokenIcon size={28} />
      </span>
      <div className="dl-text min-w-0 flex-1">
        <div className="text-[12px] font-semibold uppercase tracking-[0.12em] text-muted">{t('dashboard.home.planHint.kicker')}</div>
        <h3 className="mt-0.5 break-words font-serif text-[17px] font-medium leading-[22px] text-fg">
          {t(`dashboard.home.planHint.${reason}`)}
        </h3>
        <p className="mt-0.5 text-[14px] leading-5 text-muted">
          {limit ? t(`dashboard.home.planHint.${reason}Next`, { plan: next.title, limit }) : t('dashboard.home.planHint.midnight')}
        </p>
      </div>
      <div className="dl-action flex-none">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            trackEvent('tariff_hint_click', { place: 'home', reason });
            navigate(paths.plans);
          }}
        >
          {t('nav.plans')}
        </Button>
      </div>
    </div>
  );
}
